import { createFileRoute } from '@tanstack/react-router';
import { useState, useCallback, useEffect } from 'react';
import { useMembers, useMemberStats, useCreateMember, useUpdateMember, useDeleteMember, useTiers, useSiteSettings, useUpdateSiteSettings, membersApi } from '@wordpuppi/shared';
import type { MemberStatus, Member } from '@wordpuppi/shared';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { saveBlob } from '@/utils/desktopActions';

export const Route = createFileRoute('/sites/$siteSlug/members')({
  component: MembersPage,
});

/** RFC 4180 cell: quote when it contains a comma, quote or newline; double inner quotes. */
function csvCell(value: string | null | undefined): string {
  const s = value ?? '';
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function MembersPage() {
  const { siteSlug } = Route.useParams();
  const [statusFilter, setStatusFilter] = useState<MemberStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string> | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Settings (for public member count toggle)
  const { data: settingsData } = useSiteSettings(siteSlug);
  const updateSettings = useUpdateSiteSettings(siteSlug);

  // Queries
  const { data: statsData, isLoading: loadingStats } = useMemberStats(siteSlug);
  const { data: membersData, isLoading: loadingMembers } = useMembers(siteSlug, {
    status: statusFilter === 'all' ? undefined : statusFilter,
    limit,
    offset: (page - 1) * limit,
  });
  const { data: tiersData } = useTiers(siteSlug);

  // Mutations
  const deleteMember = useDeleteMember(siteSlug);

  const stats = statsData?.data;
  const members = membersData?.data ?? [];
  const tiers = tiersData?.data ?? [];
  const totalPages = Math.ceil((stats?.total_members ?? 0) / limit);

  // Clear selection on page/filter change
  useEffect(() => {
    setSelectedIds(new Set());
    setLastSelectedId(null);
  }, [page, statusFilter]);

  const handleRowClick = useCallback((id: string, event: React.MouseEvent) => {
    event.preventDefault();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (event.shiftKey && lastSelectedId && members.length > 0) {
        const currentIndex = members.findIndex((m) => m.id === id);
        const lastIndex = members.findIndex((m) => m.id === lastSelectedId);
        if (currentIndex !== -1 && lastIndex !== -1) {
          const start = Math.min(currentIndex, lastIndex);
          const end = Math.max(currentIndex, lastIndex);
          for (let i = start; i <= end; i++) {
            next.add(members[i].id);
          }
        }
      } else {
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
      }
      return next;
    });
    setLastSelectedId(id);
  }, [lastSelectedId, members]);

  const selectAll = () => setSelectedIds(new Set(members.map((m) => m.id)));
  const clearSelection = () => {
    setSelectedIds(new Set());
    setLastSelectedId(null);
  };

  const handleDeleteSingle = (member: Member, e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingDeleteIds(new Set([member.id]));
    setDeleteDialogOpen(true);
  };

  const handleDeleteSelected = () => {
    setPendingDeleteIds(null); // use selectedIds
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    const idsToDelete = pendingDeleteIds ?? selectedIds;
    if (idsToDelete.size === 0) return;

    setBulkDeleting(true);
    try {
      await Promise.all([...idsToDelete].map((id) => deleteMember.mutateAsync(id)));
      const count = idsToDelete.size;
      toast.success(`${count} member${count !== 1 ? 's' : ''} deleted`);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of idsToDelete) next.delete(id);
        return next;
      });
      setLastSelectedId(null);
      setDeleteDialogOpen(false);
      setPendingDeleteIds(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete members');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleEditClick = (member: Member, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingMember(member);
    setEditDialogOpen(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const deleteCount = (pendingDeleteIds ?? selectedIds).size;

  const handleExport = async () => {
    setExporting(true);
    try {
      // ponytail: no /export.csv endpoint — the list endpoint has no server-side
      // limit cap, so one big page IS the whole export. Add a streaming endpoint
      // if a site ever outgrows 10k members.
      const all = await membersApi.list(siteSlug, {
        limit: 10000,
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      const rows = all.data ?? [];
      if (rows.length === 0) {
        toast.error('No members to export');
        return;
      }
      const tierName = new Map(tiers.map((t) => [t.id, t.name]));
      const csv = [
        'email,name,status,tier,email_verified_at,created_at,updated_at,id',
        ...rows.map((m) =>
          [
            m.email,
            m.name,
            m.status,
            m.subscription_tier_id
              ? tierName.get(m.subscription_tier_id) ?? m.subscription_tier_id
              : '',
            m.email_verified_at,
            m.created_at,
            m.updated_at,
            m.id,
          ]
            .map(csvCell)
            .join(',')
        ),
      ].join('\r\n');

      // Leading BOM so Excel opens the UTF-8 correctly. saveBlob branches on
      // desktop (native save dialog — WKWebView ignores <a download>).
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
      const filename = `${siteSlug}-members-${new Date().toISOString().slice(0, 10)}.csv`;
      if (await saveBlob(blob, filename, { name: 'CSV', extensions: ['csv'] })) {
        toast.success(`Exported ${rows.length} member${rows.length !== 1 ? 's' : ''}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="mb-6 flex flex-wrap justify-between items-center gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Members</h1>
          {!loadingStats && stats ? (
            <p className="text-sm text-muted-foreground mt-1">
              {stats.total_members} member{stats.total_members !== 1 ? 's' : ''} ·{' '}
              {stats.active_members} active · {stats.verified_members} verified ·{' '}
              {stats.unsubscribed_members} unsubscribed
            </p>
          ) : (
            <p className="text-muted-foreground mt-1">
              Newsletter subscribers and site members
            </p>
          )}
        </div>
        <Button onClick={() => setAddDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Member
        </Button>
      </div>

      {/* Public member count toggle */}
      <div className="flex items-center gap-2 mb-6">
        <Switch
          id="public-member-count"
          checked={settingsData?.data?.public_member_count ?? false}
          onCheckedChange={(checked) =>
            updateSettings.mutate({ public_member_count: checked })
          }
        />
        <Label htmlFor="public-member-count" className="text-sm font-normal cursor-pointer">
          Show member count publicly
        </Label>
      </div>

      {/* Filter */}
      <div className="mb-4 flex items-center gap-2">
        <Label htmlFor="status-filter" className="text-sm text-muted-foreground">
          Status:
        </Label>
        <Select
          value={statusFilter}
          onValueChange={(v: string) => {
            setStatusFilter(v as MemberStatus | 'all');
            setPage(1);
          }}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
            <SelectItem value="bounced">Bounced</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk action bar — only when there are members to act on */}
      {members.length > 0 && (
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4 p-3 bg-muted/50 rounded-lg">
        {/* Selection controls left, export right. */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={selectAll}
          >
            Select All
          </Button>
          {selectedIds.size > 0 && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={clearSelection}
              >
                Clear Selection
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteSelected}
              >
                Delete Selected
              </Button>
              <span className="text-sm font-medium">{selectedIds.size} selected</span>
            </>
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleExport}
          disabled={exporting || loadingMembers}
          title="Export CSV"
        >
          <Download className="w-4 h-4 mr-2" />
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      </div>
      )}

      {/* Members Table */}
      {loadingMembers ? (
        <div className="text-center py-8 text-muted-foreground">
          Loading...
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-12 bg-muted rounded-lg text-muted-foreground">
          {statusFilter === 'all' ? 'No members yet.' : `No ${statusFilter} members.`}
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => (
                <TableRow
                  key={member.id}
                  className={cn(
                    'cursor-pointer',
                    selectedIds.has(member.id) && 'bg-primary/10'
                  )}
                  onClick={(e) => handleRowClick(member.id, e)}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(member.id)}
                      readOnly
                      tabIndex={-1}
                      aria-label="Select member"
                      className="pointer-events-none"
                    />
                  </TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell className={member.name ? '' : 'text-muted-foreground'}>
                    {member.name || '-'}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={member.status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(member.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={(e) => handleEditClick(member, e)}
                        title="Edit member"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={(e) => handleDeleteSingle(member, e)}
                        title="Delete member"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <Button
            variant="secondary"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground px-4">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="secondary"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Add Member Dialog */}
      <AddMemberDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        siteSlug={siteSlug}
        tiers={tiers}
      />

      {/* Edit Member Dialog */}
      <EditMemberDialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) setEditingMember(null);
        }}
        siteSlug={siteSlug}
        member={editingMember}
        tiers={tiers}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) setPendingDeleteIds(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteCount} member{deleteCount !== 1 ? 's' : ''}?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. The selected member{deleteCount !== 1 ? 's' : ''} will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => { setDeleteDialogOpen(false); setPendingDeleteIds(null); }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={bulkDeleting}
            >
              {bulkDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Add Member Dialog
// ============================================================================

function AddMemberDialog({
  open,
  onOpenChange,
  siteSlug,
  tiers,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteSlug: string;
  tiers: { id: string; name: string; is_default?: boolean }[];
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [tierId, setTierId] = useState('');
  const createMember = useCreateMember(siteSlug);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setName('');
      setTierId('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      await createMember.mutateAsync({
        email: email.trim(),
        name: name.trim() || undefined,
        subscription_tier_id: tierId || undefined,
      });
      toast.success('Member added');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add member');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Member</DialogTitle>
            <DialogDescription>
              Add a new member to this site.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="add-email">Email</Label>
              <Input
                id="add-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="member@example.com"
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-name">Name</Label>
              <Input
                id="add-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Optional"
              />
            </div>
            {tiers.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="add-tier">Tier</Label>
                <Select value={tierId} onValueChange={setTierId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Free (default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Free</SelectItem>
                    {tiers.filter((t) => !t.is_default).map((tier) => (
                      <SelectItem key={tier.id} value={tier.id}>
                        {tier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMember.isPending || !email.trim()}>
              {createMember.isPending ? 'Adding...' : 'Add Member'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Edit Member Dialog
// ============================================================================

function EditMemberDialog({
  open,
  onOpenChange,
  siteSlug,
  member,
  tiers,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteSlug: string;
  member: Member | null;
  tiers: { id: string; name: string; is_default?: boolean }[];
}) {
  const [name, setName] = useState('');
  const [status, setStatus] = useState<MemberStatus>('active');
  const [tierId, setTierId] = useState('');
  const updateMember = useUpdateMember(siteSlug);

  useEffect(() => {
    if (member) {
      setName(member.name ?? '');
      setStatus(member.status);
      setTierId(member.subscription_tier_id ?? '');
    }
  }, [member]);

  if (!member) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateMember.mutateAsync({
        memberId: member.id,
        data: {
          name: name.trim() || undefined,
          status,
          subscription_tier_id: tierId || undefined,
        },
      });
      toast.success('Member updated');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update member');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Member</DialogTitle>
            <DialogDescription>{member.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Optional"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <Select value={status} onValueChange={(v: string) => setStatus(v as MemberStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                  <SelectItem value="bounced">Bounced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {tiers.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="edit-tier">Tier</Label>
                <Select value={tierId} onValueChange={setTierId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Free (default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Free</SelectItem>
                    {tiers.filter((t) => !t.is_default).map((tier) => (
                      <SelectItem key={tier.id} value={tier.id}>
                        {tier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMember.isPending}>
              {updateMember.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Helper Components
// ============================================================================

function StatusBadge({ status }: { status: MemberStatus }) {
  const variant = status === 'active' ? 'success' : status === 'bounced' ? 'destructive' : 'secondary';

  return (
    <Badge variant={variant} className="capitalize">
      {status}
    </Badge>
  );
}
